import type EmberArray from '@ember/array';
import { A } from '@ember/array';
import { fn } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Side } from 'ember-tooltips/components/ember-popover';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import type Invite from 'garaje/models/invite';
import type UserDatum from 'garaje/models/user-datum';
import { DISCARDED_RESPONSE_STRINGS } from 'garaje/utils/enums';
import { reads } from 'macro-decorators';

interface InvitesTableInviteListPlainCellSignature {
  Args: {
    content?: Invite;
    delay?: number;
    lineHeight?: number;
    lines?: number;
    name?: string;
    popperContainer?: string;
    side?: string;
    text?: string;
    tooltipClass?: string;
  };
  Blocks: {
    default: [data: string];
  };
  Element: HTMLTableCellElement;
}

export default class InvitesTableInviteListPlainCell extends Component<InvitesTableInviteListPlainCellSignature> {
  @tracked textWasTruncated: boolean = false;

  @reads('args.delay', 400) delay!: number;
  @reads('args.lineHeight', 1.25) lineHeight!: number;
  @reads('args.lines', 2) lines!: number;
  @reads('args.popperContainer', '.ui__page') popperContainer!: string;
  @reads('args.side', 'top') side!: Side;
  @reads('args.tooltipClass', 'break-word max-w-sm z-50') tooltipClass!: string;

  contentElementId = guidFor(this);

  get data(): string {
    const { text, content, name } = this.args;

    if (text) return text;
    if (!(content && name)) return '';

    const userData: EmberArray<UserDatum> = A(content['userData'] ?? []);
    const field = userData.findBy('field', name);

    if (field) {
      const value = field?.value ?? '';

      return value === DISCARDED_RESPONSE_STRINGS.PLACEHOLDER ? `${DISCARDED_RESPONSE_STRINGS.DISPLAY_TEXT}` : value;
    }

    return '';
  }

  <template>
    {{#if (has-block)}}
      {{yield this.data}}
    {{else}}
      <td ...attributes data-test-invites-list-plain-cell>
        <div class="max-w-full not-sr-only" id={{this.contentElementId}} data-test-invites-list-plain-cell-content>
          <LineClamp
            @useNative={{true}}
            @handleTruncate={{fn (mut this.textWasTruncated)}}
            @lines={{this.lines}}
            @lineHeight={{this.lineHeight}}
            @text={{this.data}}
            aria-hidden="true"
          />
        </div>

        <div class="sr-only">
          {{! Full text always available to screen reader }}
          {{this.data}}
        </div>

        {{#if this.textWasTruncated}}
          <EmberPopover
            @delay={{this.delay}}
            @popperContainer={{this.popperContainer}}
            @side={{this.side}}
            @targetId={{this.contentElementId}}
            @text={{this.data}}
            @tooltipClass={{this.tooltipClass}}
            data-test-invites-list-plain-cell-tooltip
          />
        {{/if}}
      </td>
    {{/if}}
  </template>
}
